import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { Annotation, Deck } from "@deckloom/core/schema";
import type { ClientMessage, ServerMessage } from "@deckloom/core/protocol";
import { deckSteps, navigate, walkElements, type NavAction } from "@deckloom/core/timeline";
import { DEFAULT_TOOL, Stage, type InkTool, type NavigationTarget, type ShapeKind, type StageHandle, type ToolKind } from "@deckloom/player";
import { api, type DeckInfo } from "../api";
import { RoomClient } from "../room";
import { Toolbar } from "./Toolbar";
import { ApiKeys } from "./ApiKeys";

interface Position { page: number; step: number }

/** Mark media elements playable/unplayable from the live transcode status (deck.json is written before transcodes finish). */
function applyMediaStatus(deck: Deck, rows: { media_id: string; transcode_status: string }[]): Deck {
  const byId = new Map(rows.map((r) => [r.media_id, r.transcode_status]));
  let changed = false;
  const pages = deck.pages.map((p) => ({
    ...p,
    elements: p.elements.map((e) => {
      if (e.kind !== "media" || !e.source) return e;
      const st = byId.get(e.source.mediaId);
      const playable = st === "ready" || st === "none" ? "native" : st === "failed" || st === "skipped" ? "no" : e.source.playable;
      if (playable === e.source.playable) return e;
      changed = true;
      return { ...e, source: { ...e.source, playable } };
    }),
  }));
  return changed ? { ...deck, pages } : deck;
}

/** Unpublished drawings are kept in this browser only, per deck, so a reload does not lose them. */
function loadDrafts(deckId: string): Record<string, Annotation[]> {
  try {
    const raw = localStorage.getItem(`deckloom:drafts:${deckId}`);
    const v = raw ? (JSON.parse(raw) as unknown) : null;
    return v && typeof v === "object" ? (v as Record<string, Annotation[]>) : {};
  } catch {
    return {};
  }
}

function saveDrafts(deckId: string, drafts: Record<string, Annotation[]>): void {
  try {
    const kept = Object.fromEntries(Object.entries(drafts).filter(([, list]) => list.length > 0));
    if (Object.keys(kept).length === 0) localStorage.removeItem(`deckloom:drafts:${deckId}`);
    else localStorage.setItem(`deckloom:drafts:${deckId}`, JSON.stringify(kept));
  } catch {
    /* storage full or blocked: drafts just stay in memory */
  }
}

function deckHasMedia(deck: Deck): boolean {
  for (const p of deck.pages) for (const e of walkElements(p.elements)) if (e.kind === "media") return true;
  return false;
}

/** The tool options a frame's parent sends (`deckloom:tool`): the fields it got right, the rest left as they are. */
function toolOptsOf(o: unknown): Partial<InkTool> {
  if (!o || typeof o !== "object") return {};
  const v = o as { color?: { hex?: unknown; alpha?: unknown }; width?: unknown; shape?: unknown; fill?: unknown };
  const patch: Partial<InkTool> = {};
  if (v.color && typeof v.color.hex === "string" && /^#[0-9a-f]{6}$/i.test(v.color.hex) && typeof v.color.alpha === "number" && v.color.alpha >= 0 && v.color.alpha <= 1) patch.color = { hex: v.color.hex, alpha: v.color.alpha };
  if (typeof v.width === "number" && v.width > 0 && v.width <= 100) patch.width = v.width;
  if (v.shape === "rect" || v.shape === "ellipse" || v.shape === "line" || v.shape === "arrow") patch.shape = v.shape;
  if (typeof v.fill === "boolean") patch.fill = v.fill;
  return patch;
}

export function DeckPage() {
  const { id = "" } = useParams();
  const [search] = useSearchParams();
  const token = search.get("p");
  /** embed=1: no bar, the slide fills the page (apps put the player in a frame sized to the deck's aspect ratio) */
  const embed = search.get("embed") === "1";
  /**
   * solo=1: the deck on your own, off the room: nobody follows, everyone turns the pages and steps by hand. For the
   * operator it is where the deck is managed: the working copy (the head revision) is shown, the notes are edited
   * into it, the API panel holds the keys and the history with the Apply that makes a revision live, and the
   * drawings go to the deck's layer through its API (the room shows them at once).
   */
  const solo = search.get("solo") === "1";
  const [info, setInfo] = useState<DeckInfo | null>(null);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pos, setPos] = useState<Position>({ page: 0, step: -1 });
  const [role, setRole] = useState<"operator" | "viewer">("viewer");
  const [viewers, setViewers] = useState(0);
  const [strokes, setStrokes] = useState<Record<string, Annotation[]>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  /** draft mode: new drawings stay private until published */
  const [draftMode, setDraftMode] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Annotation[]>>(() => loadDrafts(id));
  /** the tool options persist while the tool itself is toggled on and off */
  const [toolOpts, setToolOpts] = useState<InkTool>(DEFAULT_TOOL);
  const [toolOn, setToolOn] = useState(false);
  const tool = toolOn ? toolOpts : null;
  const [showNotes, setShowNotes] = useState(false);
  /** solo mode's notes: typed here, saved into the working copy on Save (an edit: a new revision) */
  const [noteDraft, setNoteDraft] = useState<{ page: string; text: string } | null>(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState("");
  /** solo mode: a drawing the API would not take (shown in the bar until the next one goes through) */
  const [saveError, setSaveError] = useState("");
  /** the operator's API panel (takes the notes panel's place) */
  const [showApi, setShowApi] = useState(false);
  const operator = role === "operator";
  /** who turns the pages here: the operator in the room, everyone on their own */
  const drives = operator || solo;
  /** the revision shown: the live one in the room, the head (working copy) in solo mode */
  const shownRevision = (i: DeckInfo) => (solo ? i.revision : i.liveRevision);
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const [mutedFallback, setMutedFallback] = useState(false);
  const roomRef = useRef<RoomClient | null>(null);
  const stageRef = useRef<StageHandle>(null);
  const pendingSync = useRef<ServerMessage[]>([]);
  const authorId = useMemo(() => crypto.randomUUID(), []);
  /** revision of the deck.json we show; a reconnect reloads the deck when the room reports a newer one */
  const revisionRef = useRef<number | null>(null);

  // Load deck metadata, deck.json and layers.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const i = await api.deck(id, token);
        if (!alive) return;
        setInfo(i);
        setRole(i.role);
        revisionRef.current = shownRevision(i);
        if (i.status === "ready") {
          const [d0, layers, status] = await Promise.all([solo ? api.deckJsonAt(id, i.revision) : api.deckJson(id), api.layers(id), api.mediaStatus(id).catch(() => [])]);
          if (!alive) return;
          const d = applyMediaStatus(d0, status);
          setDeck(d);
          setStrokes(layers.strokes);
          if (i.role === "viewer" && deckHasMedia(d)) setNeedsUnlock(true);
          // media sync needs a server clock; decks without media let the room sleep
          roomRef.current?.setClockSync(deckHasMedia(d));
        }
      } catch (err) {
        setError((err as Error).message);
      }
    })();
    return () => { alive = false; };
  }, [id, token, solo]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The slides again: the live ones after a revision was applied, the working copy after an edit in solo mode. */
  const reloadDeck = useCallback(() => {
    api.deck(id, token).then(async (i) => {
      setInfo(i);
      revisionRef.current = shownRevision(i);
      const [d, status] = await Promise.all([solo ? api.deckJsonAt(id, i.revision) : api.deckJson(id), api.mediaStatus(id).catch(() => [])]);
      setDeck(applyMediaStatus(d, status));
    }).catch((e) => setError(String(e)));
  }, [id, token, solo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Solo mode has no stream: the working copy is looked at now and then, so an edit made elsewhere (an AI at the API) shows up.
  useEffect(() => {
    if (!solo || !operator) return;
    const t = setInterval(() => {
      api.deck(id, token).then((i) => { if (revisionRef.current !== null && i.revision !== revisionRef.current) reloadDeck(); }).catch(() => undefined);
    }, 4000);
    return () => clearInterval(t);
  }, [solo, operator, id, token, reloadDeck]);

  // Room socket (none in solo mode: the deck on your own).
  useEffect(() => {
    if (solo) return;
    const room = new RoomClient(id, token);
    roomRef.current = room;
    room.onRefused = (reason) => setError(reason);
    let connectedBefore = false;
    const off = room.on((msg: ServerMessage) => {
      switch (msg.type) {
        case "state":
          // A reconnect: catch up on what changed while we were away (drawings, notes, edits).
          if (connectedBefore) {
            api.layers(id).then((l) => { setStrokes(l.strokes); setNotes(l.notes); }).catch(() => undefined);
            if (msg.revision !== undefined && revisionRef.current !== null && msg.revision !== revisionRef.current) reloadDeck();
          }
          connectedBefore = true;
          setPos({ page: msg.page, step: msg.step });
          setRole(msg.role);
          setViewers(msg.viewers);
          for (const sync of msg.mediaSync) pendingSync.current.push({ type: "mediaSync", sync });
          break;
        case "goto":
          setPos({ page: msg.page, step: msg.step });
          break;
        case "presence":
          setViewers(msg.count);
          break;
        case "media":
          stageRef.current?.applyMediaSync(
            { elementId: msg.elementId, position: msg.position, playing: msg.action === "play", serverTime: msg.serverTime },
            room.serverNow(),
          );
          break;
        case "mediaSync":
          stageRef.current?.applyMediaSync(msg.sync, room.serverNow());
          break;
        case "mediaReady":
          setDeck((d) => (d ? applyMediaStatus(d, [{ media_id: msg.mediaId, transcode_status: "ready" }]) : d));
          break;
        case "stroke.add":
          setStrokes((s) => ({ ...s, [msg.page]: [...(s[msg.page] ?? []).filter((x) => x.id !== msg.stroke.id), msg.stroke] }));
          break;
        case "stroke.undo":
          setStrokes((s) => ({ ...s, [msg.page]: (s[msg.page] ?? []).filter((x) => x.id !== msg.strokeId) }));
          break;
        case "stroke.clear":
          setStrokes((s) => ({ ...s, [msg.page]: [] }));
          break;
        case "trigger":
          stageRef.current?.applyTrigger(msg.seq);
          break;
        case "deck.changed":
          // a revision was applied: load it (the room sends a goto if our page moved)
          revisionRef.current = msg.revision;
          reloadDeck();
          break;
        case "status":
          if (msg.status === "ready") {
            api.deckJson(id).then((d) => { setDeck(d); room.setClockSync(deckHasMedia(d)); }).catch((e) => setError(String(e)));
            api.deck(id, token).then(setInfo).catch(() => undefined);
          }
          if (msg.status === "failed") setError(msg.error ?? "conversion failed");
          break;
      }
    });
    room.connect();
    return () => { off(); room.close(); roomRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, solo]);

  /**
   * Where the player's messages go: the room while in it. In solo mode the operator's drawings go to the deck's
   * API (the same layer the room shows); the rest (page moves, media) stays here.
   */
  const send = useCallback((msg: ClientMessage) => {
    if (!solo) return roomRef.current?.send(msg);
    if (!token) return;
    const report = (e: Error) => setSaveError(`not saved: ${e.message}`);
    const saved = () => setSaveError("");
    if (msg.type === "stroke.add") api.addDrawing(id, token, msg.page, msg.stroke).then(saved, report);
    else if (msg.type === "stroke.remove") api.deleteDrawing(id, token, msg.page, msg.strokeId).then(saved, report);
    else if (msg.type === "stroke.clear") api.clearDrawings(id, token, msg.page).then(saved, report);
  }, [solo, token, id]);

  // Apply media sync that arrived before the stage mounted (late joiners).
  useEffect(() => {
    if (!deck || !stageRef.current) return;
    const room = roomRef.current;
    const t = setTimeout(() => {
      for (const m of pendingSync.current) if (m.type === "mediaSync" && room) stageRef.current?.applyMediaSync(m.sync, room.serverNow());
      pendingSync.current = [];
    }, 300);
    return () => clearTimeout(t);
  }, [deck, pos.page]);

  // The operator's heartbeat for playing media (the room's viewers follow it).
  useEffect(() => {
    if (!operator || solo) return;
    const t = setInterval(() => {
      for (const s of stageRef.current?.mediaSnapshot() ?? []) roomRef.current?.send({ type: "mediaSync", ...s });
    }, 2000);
    return () => clearInterval(t);
  }, [operator, solo]);

  // clamped: after an edit the new position can arrive a moment before the new deck
  const pageIndex = deck ? Math.min(pos.page, deck.pages.length - 1) : 0;
  const page = deck?.pages[pageIndex];

  const goto = useCallback((next: Position) => {
    setPos(next);
    send({ type: "goto", page: next.page, step: next.step });
  }, [send]);

  const pageSteps = useMemo(() => (deck ? deckSteps(deck) : []), [deck]);

  /** Same rules the room applies to API calls (see navigate in @deckloom/core/timeline). */
  const move = useCallback((nav: NavAction) => {
    if (!drives) return;
    const to = navigate(pageSteps, pos, nav);
    if (to && (to.page !== pos.page || to.step !== pos.step)) goto(to);
  }, [drives, pageSteps, pos, goto]);
  const advance = useCallback(() => move({ action: "next" }), [move]);
  const back = useCallback(() => move({ action: "prev" }), [move]);

  /** Switch to a tool (toggling it off when it is already active), optionally overriding options. */
  const pickTool = useCallback((kind: ToolKind, opts?: Partial<InkTool>) => {
    setToolOpts((t) => ({ ...t, ...opts, kind }));
    setToolOn((on) => !(on && toolOpts.kind === kind && !opts));
  }, [toolOpts.kind]);

  // Hyperlinks on shapes (whoever turns the pages; the room's viewers follow).
  const onNavigate = useCallback((t: NavigationTarget) => {
    switch (t.kind) {
      case "page": move({ action: "goto", pageId: t.pageId }); break;
      case "next": move({ action: "next" }); break;
      case "prev": move({ action: "prev" }); break;
      case "first": move({ action: "first" }); break;
      case "last": case "end": move({ action: "last" }); break;
    }
  }, [move]);

  // Automatic slide advance (transition advTm) once the page has shown all its steps.
  useEffect(() => {
    if (!deck || !drives) return;
    const p = deck.pages[pos.page];
    const after = p?.transition?.advanceAfter;
    if (after == null) return;
    if (pos.step < (pageSteps[pos.page]?.count ?? 0) - 1) return;
    if (pos.page >= deck.pages.length - 1) return;
    const t = setTimeout(() => advance(), Math.max(0, after));
    return () => clearTimeout(t);
  }, [deck, drives, pos, pageSteps, advance]);

  useEffect(() => saveDrafts(id, drafts), [id, drafts]);
  const pageDrafts = (page && drafts[page.id]) || [];

  const addStroke = useCallback((pageId: string, stroke: Annotation) => {
    if (draftMode) return setDrafts((d) => ({ ...d, [pageId]: [...(d[pageId] ?? []), stroke] }));
    setStrokes((s) => ({ ...s, [pageId]: [...(s[pageId] ?? []), stroke] }));
    send({ type: "stroke.add", page: pageId, stroke });
  }, [draftMode, send]);

  const removeStroke = useCallback((pageId: string, strokeId: string) => {
    if ((drafts[pageId] ?? []).some((a) => a.id === strokeId)) {
      return setDrafts((d) => ({ ...d, [pageId]: (d[pageId] ?? []).filter((a) => a.id !== strokeId) }));
    }
    setStrokes((s) => ({ ...s, [pageId]: (s[pageId] ?? []).filter((x) => x.id !== strokeId) }));
    send({ type: "stroke.remove", page: pageId, strokeId });
  }, [drafts, send]);

  /** In draft mode undo takes back the last draft first. The room undoes the last drawing itself; alone, we know which it is. */
  const undo = useCallback((pageId: string) => {
    if (draftMode && (drafts[pageId]?.length ?? 0) > 0) return setDrafts((d) => ({ ...d, [pageId]: (d[pageId] ?? []).slice(0, -1) }));
    if (!solo) return send({ type: "stroke.undo", page: pageId });
    const last = strokes[pageId]?.[(strokes[pageId]?.length ?? 0) - 1];
    if (last) removeStroke(pageId, last.id);
  }, [draftMode, drafts, solo, send, strokes, removeStroke]);

  /** Show this page's drafts to everyone, in the order they were drawn. */
  const publish = useCallback((pageId: string) => {
    const list = drafts[pageId] ?? [];
    if (list.length === 0) return;
    setStrokes((s) => ({ ...s, [pageId]: [...(s[pageId] ?? []), ...list] }));
    for (const stroke of list) send({ type: "stroke.add", page: pageId, stroke });
    setDrafts((d) => ({ ...d, [pageId]: [] }));
  }, [drafts, send]);
  const discard = useCallback((pageId: string) => setDrafts((d) => ({ ...d, [pageId]: [] })), []);

  // Keyboard control for whoever turns the pages (the drawing keys only matter where drawing is on).
  useEffect(() => {
    if (!drives) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT" || tag === "VIDEO" || tag === "AUDIO") return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown" || e.key === "Enter") { e.preventDefault(); advance(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp" || e.key === "Backspace") { e.preventDefault(); back(); }
      else if (e.key === "Home") move({ action: "first" });
      else if (e.key === "End") move({ action: "last" });
      else if (e.key === "n") setShowNotes((v) => !v);
      else if (e.key === "p") pickTool("draw", { color: { hex: "#e11d48", alpha: 1 }, width: 3 });
      else if (e.key === "m") pickTool("draw", { color: { hex: "#facc15", alpha: 0.4 }, width: 18 });
      else if (e.key === "s") pickTool("shape");
      else if (e.key === "t") pickTool("text");
      else if (e.key === "e") pickTool("eraser");
      else if (e.key === "z" && (e.metaKey || e.ctrlKey)) undo(deck!.pages[pos.page]!.id);
      else if (e.key === "d") setDraftMode((v) => !v);
      else if (e.key === "Escape") setToolOn(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drives, advance, back, move, deck, pickTool, pos.page, undo]);

  /** drawing is the operator's (viewers only watch) */
  const canDraw = operator;
  const clearPage = useCallback((pageId: string) => { setStrokes((s) => ({ ...s, [pageId]: [] })); send({ type: "stroke.clear", page: pageId }); }, [send]);
  /** the notes of a page: the slide's own (edited in solo mode into the working copy) */
  const notesOf = (p: Deck["pages"][number]) => notes[p.id] ?? p.notes.plain;

  // In a frame, the app around it sizes the frame to the deck (deckloom.io's stage box): it hears the size here, and the
  // page shown with its notes (`deckloom:page`); it may pick the operator's drawing tool (`{ type: "deckloom:tool", kind, opts }`,
  // kind null for none, opts the colour, width, shape and fill to use, each optional) and undo or clear the page
  // (`{ type: "deckloom:action", action }`), the bar being hidden in a frame.
  useEffect(() => {
    if (embed && deck && window.parent !== window) window.parent.postMessage({ type: "deckloom:deck", size: { w: deck.size.w, h: deck.size.h } }, "*");
  }, [embed, deck?.size.w, deck?.size.h]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (embed && page && window.parent !== window) window.parent.postMessage({ type: "deckloom:page", index: pageIndex, id: page.id, notes: notesOf(page) }, "*");
  }, [embed, page, pageIndex, notes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!embed || window.parent === window) return;
    const on = (e: MessageEvent) => {
      if (e.source !== window.parent) return;
      const d = e.data as { type?: unknown; kind?: unknown; opts?: unknown; action?: unknown } | null;
      if (d?.type === "deckloom:tool") {
        const kind = d.kind;
        const patch = toolOptsOf(d.opts);
        if (kind === null) { setToolOpts((t) => ({ ...t, ...patch })); setToolOn(false); }
        else if (kind === "draw" || kind === "shape" || kind === "text" || kind === "eraser") { setToolOpts((t) => ({ ...t, ...patch, kind })); setToolOn(true); }
      } else if (d?.type === "deckloom:action" && page) {
        if (d.action === "undo") undo(page.id);
        else if (d.action === "clear") clearPage(page.id);
      }
    };
    addEventListener("message", on);
    return () => removeEventListener("message", on);
  }, [embed, page, undo, clearPage]);

  const saveNote = async () => {
    if (!noteDraft || !token) return;
    setNoteSaving(true); setNoteError("");
    try {
      await api.setNote(id, token, noteDraft.page, noteDraft.text);
      setNotes((n) => ({ ...n, [noteDraft.page]: noteDraft.text }));
      setNoteDraft(null);
      reloadDeck();
    } catch (e) { setNoteError((e as Error).message); }
    finally { setNoteSaving(false); }
  };

  const mediaUrl = useMemo(() => api.mediaUrl(id), [id]);
  const playUrl = useMemo(() => api.playUrl(id), [id]);
  const onMediaEvent = useCallback((elementId: string, action: "play" | "pause" | "seek" | "stop", position: number) => {
    send({ type: "media", elementId, action, position });
  }, [send]);
  const onMutedFallback = useCallback(() => setMutedFallback(true), []);

  if (error) return <main className="center"><p className="error">{error}</p></main>;
  if (!info) return <main className="center"><p>Loading…</p></main>;
  if (info.status !== "ready" || !deck || !page) {
    return (
      <main className="center">
        <h1>{info.title || "Deck"}</h1>
        {info.status === "failed" ? <p className="error">{info.error ?? "conversion failed"}</p> : <p>Converting… this page updates automatically.</p>}
      </main>
    );
  }

  const unlock = async () => {
    await stageRef.current?.unlockMedia();
    setNeedsUnlock(false);
    setMutedFallback(false);
  };

  return (
    <div className={`deck ${role}${showNotes || showApi ? " with-notes" : ""}${embed ? " embed" : ""}`} style={{ ["--slide-aspect" as string]: `${deck.size.w} / ${deck.size.h}` }}>
      <div className="stage-wrap">
        <Stage
          ref={stageRef}
          deck={deck}
          pageIndex={pageIndex}
          step={pos.step}
          mediaUrl={mediaUrl}
          playUrl={playUrl}
          strokes={strokes[page.id] ?? []}
          drafts={pageDrafts}
          inkTool={canDraw ? tool : null}
          authorId={authorId}
          presenter={drives}
          onStroke={(stroke) => addStroke(page.id, stroke)}
          onStrokeRemove={(strokeId) => removeStroke(page.id, strokeId)}
          onTrigger={(seq) => { if (solo) stageRef.current?.applyTrigger(seq); else if (operator) send({ type: "trigger", page: pos.page, seq }); }}
          onNavigate={onNavigate}
          onMediaEvent={onMediaEvent}
          onMutedFallback={onMutedFallback}
          onAutoStep={(step) => drives && goto({ page: pos.page, step })}
          onClick={() => !tool && advance()}
        >
          {needsUnlock && (
            <button className="unlock" onClick={(e) => { e.stopPropagation(); void unlock(); }}>
              Tap to join with sound
            </button>
          )}
          {!needsUnlock && mutedFallback && (
            <button className="unlock small" onClick={(e) => { e.stopPropagation(); void unlock(); }} title="Sound is muted until you tap">
              🔇 Tap for sound
            </button>
          )}
        </Stage>
      </div>

      {showApi && operator && solo && token && <ApiKeys deckId={id} token={token} onChanged={reloadDeck} />}

      {showNotes && !showApi && operator && (
        <aside className="notes">
          <h3>Notes · page {pos.page + 1}</h3>
          {solo ? (
            <>
              <textarea value={noteDraft?.page === page.id ? noteDraft.text : notesOf(page)} onChange={(e) => setNoteDraft({ page: page.id, text: e.target.value })} />
              <div className="row">
                <button className="primary" disabled={!noteDraft || noteDraft.page !== page.id || noteDraft.text === notesOf(page) || noteSaving} onClick={() => void saveNote()}>{noteSaving ? "Saving…" : "Save"}</button>
                <button disabled={!noteDraft || noteDraft.page !== page.id} onClick={() => setNoteDraft(null)}>Revert</button>
                {noteError && <span className="error">{noteError}</span>}
              </div>
            </>
          ) : (
            <pre>{notesOf(page)}</pre>
          )}
        </aside>
      )}

      {!embed && (
        <footer className="bar">
          <span className="title">{info.title}{solo ? " · solo" : ""}</span>
          <span className="pos">{pos.page + 1} / {deck.pages.length}{(pageSteps[pos.page]?.count ?? 0) > 0 ? ` · step ${pos.step + 1}/${pageSteps[pos.page]!.count}` : ""}</span>
          {solo ? saveError && <span className="error" title="the drawing is here but not saved">{saveError}</span> : <span className="viewers" title="connected">{viewers} online</span>}
          {drives && (
            <>
              <button onClick={back}>‹</button>
              <button onClick={advance}>›</button>
            </>
          )}
          {canDraw && (
            <Toolbar
              tool={tool}
              opts={toolOpts}
              onPick={(kind: ToolKind) => pickTool(kind)}
              onChange={(patch: Partial<InkTool>) => { setToolOpts((t) => ({ ...t, ...patch })); setToolOn(true); }}
              onShape={(shape: ShapeKind) => { setToolOpts((t) => ({ ...t, kind: "shape", shape })); setToolOn(true); }}
              onUndo={() => undo(page.id)}
              draftMode={draftMode}
              draftCount={pageDrafts.length}
              onDraftMode={() => setDraftMode((v) => !v)}
              onPublish={() => publish(page.id)}
              onDiscard={() => discard(page.id)}
              onClear={() => clearPage(page.id)}
            />
          )}
          {operator && <button className={showNotes && !showApi ? "on" : ""} onClick={() => { setShowApi(false); setShowNotes((v) => !v || showApi); }} title="notes (n)">Notes</button>}
          {operator && solo && token && <button className={showApi ? "on" : ""} onClick={() => setShowApi((v) => !v)} title="the deck's API for scripts and AI agents, and the history of the slides">API</button>}
        </footer>
      )}
    </div>
  );
}
